import React, { useEffect, useState, useRef } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Upload } from "lucide-react";
import { LibroPagination } from "../components/LibroPagination";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TASA_RETENCION_DEFAULT = 15.25;

const EMPTY = {
  prestador_rut: "",
  prestador_nombre: "",
  folio: "",
  fecha_emision: todayISO(),
  monto_bruto: "",
  tasa_retencion: TASA_RETENCION_DEFAULT,
  observaciones: "",
  generar_asiento: true,
};

function formatPeriodo(periodo) {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo || "";
  const [y, m] = periodo.split("-");
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
}

export default function BoletasHonorarios() {
  const [data, setData] = useState(null);
  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(() => new Date().toISOString().slice(0, 7));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [reemplazar, setReemplazar] = useState(false);
  const fileRef = useRef();

  const loadPeriodos = async () => {
    try {
      const { data: list } = await api.get("/boletas-honorarios/periodos");
      setPeriodos(list || []);
    } catch {
      /* silent */
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (periodo) params.periodo = periodo;
      const { data } = await api.get("/boletas-honorarios", { params });
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPeriodos(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [periodo, page, pageSize]);

  const bruto = parseFloat(form.monto_bruto) || 0;
  const tasa = parseFloat(form.tasa_retencion) || 0;
  const retencion = Math.round(bruto * tasa / 100);
  const liquido = Math.round(bruto - retencion);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/boletas-honorarios", {
        prestador_rut: form.prestador_rut,
        prestador_nombre: form.prestador_nombre,
        folio: form.folio,
        fecha_emision: form.fecha_emision,
        monto_bruto: parseFloat(form.monto_bruto) || 0,
        tasa_retencion: parseFloat(form.tasa_retencion) || TASA_RETENCION_DEFAULT,
        observaciones: form.observaciones,
        generar_asiento: form.generar_asiento,
      });
      toast.success(form.generar_asiento ? "Boleta registrada y asiento generado" : "Boleta registrada");
      setOpenNew(false);
      setForm({ ...EMPTY, fecha_emision: periodo ? `${periodo}-01` : todayISO() });
      if (form.fecha_emision?.slice(0, 7)) {
        setPeriodo(form.fecha_emision.slice(0, 7));
      }
      setPage(1);
      loadPeriodos();
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.info("Importando honorarios…");
      const { data } = await api.post(`/boletas-honorarios/import?reemplazar=${reemplazar}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importadas ${data.imported} · Omitidas ${data.skipped}`);
      if ((data.errores || []).length) toast.warning(`${data.errores.length} error(es)`);
      if (data.periodo) setPeriodo(data.periodo);
      setPage(1);
      loadPeriodos();
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id, folio) => {
    if (!window.confirm(`¿Eliminar boleta #${folio}? También se eliminará su asiento en borrador.`)) return;
    try {
      await api.delete(`/boletas-honorarios/${id}`);
      toast.success("Boleta eliminada");
      loadPeriodos();
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <Page>
      <PageHeader
        title="Libro de honorarios"
        subtitle={`Registro mensual de boletas a terceros · Retención ${TASA_RETENCION_DEFAULT.toLocaleString("es-CL")}%`}
        testid="bh-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <input type="file" ref={fileRef} onChange={handleImport} accept=".xlsx,.xls,.csv" className="hidden" />
            <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none px-2 py-1.5 rounded-sm hover:bg-muted">
              <input type="checkbox" checked={reemplazar} onChange={(e) => setReemplazar(e.target.checked)} />
              Reemplazar duplicadas
            </label>
            <button type="button" onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 border border-warning text-warning-foreground hover:bg-warning/10 text-sm px-3 py-1.5 rounded-sm">
              <Upload className="h-4 w-4" /> Importar mes
            </button>
            <Dialog open={openNew} onOpenChange={setOpenNew}>
              <DialogTrigger asChild>
                <button data-testid="new-bh-button"
                  className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-1.5 rounded-sm">
                  <Plus className="h-4 w-4" /> Nueva boleta
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nueva boleta de honorarios</DialogTitle>
                  <DialogDescription>
                    La retención por defecto es {TASA_RETENCION_DEFAULT.toLocaleString("es-CL")}% (2026).
                    Marca la opción para generar el asiento contable automáticamente.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="form-bh">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-caps block mb-1">RUT prestador</label>
                      <input required data-testid="bh-rut" value={form.prestador_rut}
                        onChange={(e) => setForm({ ...form, prestador_rut: e.target.value })}
                        placeholder="12.345.678-9"
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Folio</label>
                      <input required data-testid="bh-folio" value={form.folio}
                        onChange={(e) => setForm({ ...form, folio: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="label-caps block mb-1">Nombre prestador</label>
                    <input required data-testid="bh-nombre" value={form.prestador_nombre}
                      onChange={(e) => setForm({ ...form, prestador_nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="label-caps block mb-1">Fecha</label>
                      <input required type="date" data-testid="bh-fecha" value={form.fecha_emision}
                        onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Monto bruto</label>
                      <input required type="number" min="1" data-testid="bh-bruto" value={form.monto_bruto}
                        onChange={(e) => setForm({ ...form, monto_bruto: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Retención %</label>
                      <input type="number" step="0.25" min="0" max="100" data-testid="bh-tasa" value={form.tasa_retencion}
                        onChange={(e) => setForm({ ...form, tasa_retencion: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
                    </div>
                  </div>

                  {bruto > 0 && (
                    <div className="bg-muted rounded-sm px-3 py-2 text-xs" data-testid="bh-preview">
                      <div className="flex justify-between"><span className="text-muted-foreground">Bruto</span><span className="font-mono">{fmtCLP(bruto)}</span></div>
                      <div className="flex justify-between text-destructive"><span>Retención ({tasa}%)</span><span className="font-mono">- {fmtCLP(retencion)}</span></div>
                      <div className="flex justify-between font-semibold border-t border-border mt-1 pt-1"><span>Líquido a pagar</span><span className="font-mono">{fmtCLP(liquido)}</span></div>
                    </div>
                  )}

                  <div>
                    <label className="label-caps block mb-1">Observaciones</label>
                    <input value={form.observaciones} data-testid="bh-obs"
                      onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                  </div>

                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" title="Crea automáticamente un asiento contable en borrador: Honorarios → Honorarios por Pagar + Retención">
                    <input type="checkbox" data-testid="bh-asiento" checked={form.generar_asiento}
                      onChange={(e) => setForm({ ...form, generar_asiento: e.target.checked })} />
                    Generar asiento contable automático (borrador)
                  </label>

                  <DialogFooter>
                    <button type="submit" data-testid="submit-bh"
                      className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">
                      Registrar boleta
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 p-3 bg-card border border-border rounded-md" data-testid="bh-periodo-bar">
        <div>
          <label className="label-caps block mb-1">Mes del libro</label>
          <input
            type="month"
            value={periodo}
            onChange={(e) => { setPeriodo(e.target.value); setPage(1); }}
            data-testid="bh-periodo"
            className="px-3 py-2 border border-border rounded-sm text-sm"
          />
        </div>
        {periodos.length > 0 && (
          <div className="min-w-[220px]">
            <label className="label-caps block mb-1">Ir a mes con boletas</label>
            <Select
              value={periodos.some((p) => p.periodo === periodo) ? periodo : undefined}
              onValueChange={(v) => { setPeriodo(v); setPage(1); }}
            >
              <SelectTrigger data-testid="bh-periodos-select">
                <SelectValue placeholder="Meses con movimiento…" />
              </SelectTrigger>
              <SelectContent>
                {periodos.map((p) => (
                  <SelectItem key={p.periodo} value={p.periodo}>
                    {formatPeriodo(p.periodo)} · {p.count} boleta(s) · {fmtCLP(p.retencion)} retención
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="text-sm text-muted-foreground pb-2">
          Mostrando <strong>{formatPeriodo(periodo)}</strong>
          {data?.totales ? ` · ${data.totales.count} boleta(s)` : ""}
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KPI label="Boletas del mes" value={data.totales.count.toLocaleString("es-CL")} />
          <KPI label="Bruto del mes" value={fmtCLP(data.totales.bruto)} />
          <KPI label="Retención del mes" value={fmtCLP(data.totales.retencion)} tone="warning" />
          <KPI label="Líquido del mes" value={fmtCLP(data.totales.liquido)} tone="primary" />
        </div>
      )}

      <div className="bg-card border border-border rounded-md overflow-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : !data || data.boletas.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No hay boletas en {formatPeriodo(periodo)}. Crea una nueva o importa el consolidado del mes.
          </div>
        ) : (
          <table className="acc-table" data-testid="bh-table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Fecha</th>
                <th style={{ width: 80 }}>Folio</th>
                <th style={{ width: 120 }}>RUT</th>
                <th>Prestador</th>
                <th className="num">Bruto</th>
                <th className="num">Retención</th>
                <th className="num">Líquido</th>
                <th style={{ width: 80 }}>Asiento</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.boletas.map((b) => (
                <tr key={b.id} data-testid={`bh-row-${b.id}`} className={b.estado === "anulado" ? "opacity-50 bg-muted/40" : ""}>
                  <td>{fmtDate(b.fecha_emision)}</td>
                  <td className="font-mono text-[12px]">
                    #{b.folio}
                    {b.estado === "anulado" && (
                      <span className="ml-1 text-[9px] font-semibold uppercase text-destructive">Anulado</span>
                    )}
                  </td>
                  <td className="font-mono text-[12px]">{b.prestador_rut}</td>
                  <td>{b.prestador_nombre}</td>
                  <td className="num">{b.estado === "anulado" ? "—" : fmtCLP(b.monto_bruto)}</td>
                  <td className="num text-destructive">{b.estado === "anulado" ? "—" : fmtCLP(b.retencion)}</td>
                  <td className="num font-semibold">{b.estado === "anulado" ? "—" : fmtCLP(b.monto_liquido)}</td>
                  <td>
                    {b.asiento_numero ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-primary">
                        <FileText className="h-3 w-3" /> #{b.asiento_numero}
                      </span>
                    ) : <span className="text-[11px] text-muted-foreground/70">—</span>}
                  </td>
                  <td>
                    <button onClick={() => del(b.id, b.folio)} data-testid={`delete-bh-${b.id}`}
                      className="text-destructive hover:bg-destructive/10 p-1 rounded-sm">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-muted font-semibold">
                <td colSpan={4} className="text-right pr-3 label-caps">Totales {formatPeriodo(periodo)}</td>
                <td className="num">{fmtCLP(data.totales.bruto)}</td>
                <td className="num text-destructive">{fmtCLP(data.totales.retencion)}</td>
                <td className="num text-primary">{fmtCLP(data.totales.liquido)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        )}
        <LibroPagination
          pagination={data?.pagination}
          pageSize={pageSize}
          onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
          onPageChange={setPage}
          testidPrefix="bh"
        />
      </div>
    </Page>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    primary: "text-primary",
    warning: "text-warning-foreground",
  };
  return (
    <div className="bg-card border border-border rounded-md p-3 sm:p-4">
      <div className="label-caps mb-1">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone] || tones.default}`}>{value}</div>
    </div>
  );
}
